import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SickIcon from '@mui/icons-material/Sick';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DataTable, { ColDef } from '../util/DataTable';
import PageToolbar from '../util/PageToolbar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import AbsenceRequestModal from './AbsenceRequestModal';
import { modalBaseStyle } from '../util/modalStyle';
import { formatDate } from '../util/formatDate';
import UserAvatar from '../util/UserAvatar';

interface Props {
  access: any;
  users: any[];
  baseUrl?: string;
  staticDateCompany?: string;
}

const STATUS_CLASS: Record<string, string> = {
  draft: 'new', pending: 'pending', approved: 'fulfilled',
  rejected: 'rejected', cancelled: 'new', recorded: 'paid',
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: number; color: string }> = ({ icon, label, value, color }) => (
  <div className="ts-card" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l2)', padding: 'var(--space-l2)', flex: 1, minWidth: 120 }}>
    <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-input)', background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-color)', lineHeight: 1.1 }}>{value}</p>
      <p style={{ margin: 0, fontSize: 11, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap' }}>{label}</p>
    </div>
  </div>
);

const HrAbsenceWorkflow: React.FC<Props> = ({ access, users, baseUrl = '', staticDateCompany }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [allowPartial, setAllowPartial] = useState(false);
  const [timeStep, setTimeStep] = useState(15);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [rejectFor, setRejectFor] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [showKpi, setShowKpi] = useState(false);
  const [showApproved, setShowApproved] = useState(false);
  const toolbarSlot = useRef<HTMLDivElement>(null);

  const canApproveAll = access?.hr_absence_approve_all === true;
  // approve_team: можно аппрувить только те, где текущий пользователь — аппрувер
  const canApproveTeam = !canApproveAll && (access?.hr_absence_approve_team === true || access?.hr_absence_approve === true);
  const canManage = canApproveAll || canApproveTeam;

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get('hr-absence');
      setRows(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [typesRes, userRes] = await Promise.allSettled([
        axios.get('hr-absence-type'),
        axios.get('user'),
      ]);
      if (typesRes.status === 'fulfilled') setTypes(Array.isArray(typesRes.value.data) ? typesRes.value.data : []);
      if (userRes.status === 'fulfilled') {
        setAllowPartial(!!userRes.value.data?.company?.hr_allowPartialDayAbsence);
        setTimeStep(userRes.value.data?.company?.hr_timeStepMinutes ?? 15);
        setCurrentUserId(userRes.value.data?.id ?? null);
      }
    } catch {}
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const getUser = (id: number) => users.find(x => x.id === id) ?? null;
  const getUserName = (id: number) => {
    const u = getUser(id);
    return u ? `${u.first_name} ${u.last_name}` : `#${id}`;
  };
  const getType = (id: number) => types.find(tp => tp.id === id);

  const doAction = async (fn: () => Promise<any>, okMsg: string) => {
    try {
      await fn();
      enqueueSnackbar(okMsg, { variant: 'success' });
      loadList();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    }
  };
  const approve = (id: number) => doAction(() => axios.put(`hr-absence/${id}/approve`), t('HrApproved') as string);
  const confirmReject = () => {
    if (rejectFor == null) return;
    const id = rejectFor; setRejectFor(null);
    doAction(() => axios.put(`hr-absence/${id}/reject`, { reason: rejectReason.trim() }), t('HrRejected') as string);
    setRejectReason('');
  };
  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row: any) => { setEditing(row); setModalOpen(true); };
  const removeRow = (id: number) => {
    if (!window.confirm(t('HrConfirmDelete', 'Delete this record?') as string)) return;
    doAction(() => axios.delete(`hr-absence/${id}`), t('HrDeleted', 'Deleted') as string);
  };

  // Stats
  const leaveCount    = rows.filter(r => r.category === 'leave').length;
  const sicknessCount = rows.filter(r => r.category === 'sickness').length;
  const latenessCount = rows.filter(r => r.category === 'lateness').length;
  const pendingCount  = rows.filter(r => r.status === 'pending').length;
  const approvedCount = rows.filter(r => r.status === 'approved').length;

  const columns: ColDef<any>[] = [
    {
      key: 'employee', label: t('Name') as string,
      render: r => {
        const u = getUser(r.userId);
        return u ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAvatar user={u} baseUrl={baseUrl} size={28} />
            <span>{u.first_name} {u.last_name}</span>
          </div>
        ) : `#${r.userId}`;
      },
      filterType: 'text', filterExtract: r => getUserName(r.userId), sortExtract: r => getUserName(r.userId),
    },
    {
      key: 'supervisor', label: t('Supervisor') as string,
      render: r => r.approverId ? getUserName(r.approverId) : '—',
      filterType: 'text', filterExtract: r => r.approverId ? getUserName(r.approverId) : '',
      sortExtract: r => r.approverId ? getUserName(r.approverId) : '',
    },
    {
      key: 'category', label: t('Category') as string,
      render: r => {
        const c = getType(r.typeId)?.color || 'var(--text-color-calendar)';
        return <span className="sbadge" style={{ background: `${c}22`, color: c }}>{r.category}</span>;
      },
      filterType: 'select', filterOptions: ['leave', 'sickness', 'lateness', 'other'].map(c => ({ value: c, label: c })),
      filterExtract: r => r.category, sortExtract: r => r.category,
    },
    { key: 'absenceType', label: t('HrAbsenceType') as string, render: r => getType(r.typeId)?.name || '—' },
    {
      key: 'period', label: t('Period') as string, render: r => `${formatDate(r.startDate, staticDateCompany)} → ${formatDate(r.endDate, staticDateCompany)}`,
      sortExtract: r => r.startDate,
      filterType: 'daterange', filterExtract: r => r.startDate ?? '',
    },
    {
      key: 'duration', label: t('Hours') as string,
      render: r => r.category === 'lateness' ? `${r.minutesLate ?? 0} ${t('Min')}` : `${r.hours}${t('HShort')} / ${r.days}${t('DShort')}`,
      sortExtract: r => r.hours,
    },
    {
      key: 'status', label: t('Status') as string,
      render: r => <span className={`sbadge ${STATUS_CLASS[r.status] ?? 'new'}`}>{t('HrStatus_' + r.status)}</span>,
      filterType: 'select', filterOptions: ['draft', 'pending', 'approved', 'rejected', 'cancelled', 'recorded'].map(s => ({ value: s, label: s })),
      filterExtract: r => r.status,
    },
    { key: 'reason', label: t('HrReason') as string, render: r => r.reason || r.rejectReason || '—', defaultVisible: false },
    { key: 'note', label: t('Note') as string, render: r => r.note || '—', defaultVisible: false },
    {
      key: 'actions', label: '', width: 'auto',
      render: r => {
        const editableStatus = r.status === 'draft' || r.status === 'pending';
        const canEditRow = canManage || (r.userId === currentUserId && editableStatus);
        const canDeleteRow = canManage;
        return (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
            {canManage && editableStatus &&
              (canApproveAll || (canApproveTeam && r.approverId === currentUserId)) && (
              <>
                <button type="button" onClick={() => approve(r.id)}
                  style={{ fontSize: 11, fontWeight: 600, height: 26, padding: '0 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: '#43c98a', color: '#fff', whiteSpace: 'nowrap' }}>
                  {t('HrApprove')}
                </button>
                <button type="button" onClick={() => setRejectFor(r.id)}
                  style={{ fontSize: 11, fontWeight: 600, height: 26, padding: '0 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: '#ef4444', color: '#fff', whiteSpace: 'nowrap' }}>
                  {t('HrReject')}
                </button>
              </>
            )}
            {canEditRow && (
              <button type="button" className="btn-icon" title={t('Edit') as string} onClick={() => openEdit(r)}>✎</button>
            )}
            {canDeleteRow && (
              <button type="button" className="btn-icon" title={t('Delete') as string} style={{ color: '#ef4444' }} onClick={() => removeRow(r.id)}>🗑</button>
            )}
          </div>
        );
      },
    },
  ];

  const baseRows = showApproved ? rows : rows.filter(r => r.status !== 'approved');
  const filtered = applySearchAndFilters(baseRows, search, filters, columns,
    (r: any) => [getUserName(r.userId), r.approverId ? getUserName(r.approverId) : '', r.category].filter(Boolean).join(' '));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-l1)', minHeight: 0, overflow: 'hidden' }}>

      <PageToolbar mb={0}>
        {/* Search */}
        <SearchFilter grow columns={columns} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />
        <div className="divider-v" />
        <button type="button" className="btn-primary" onClick={openAdd}>+ {t('HrAddAbsence')}</button>

        <div style={{ flex: 1 }} />

        {/* Archive toggle */}
        <button
          type="button"
          onClick={() => setShowApproved(v => !v)}
          style={{
            fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
            border: showApproved ? 'none' : '1px solid var(--border-input)',
            background: showApproved ? '#43c98a' : 'transparent',
            color: showApproved ? '#fff' : 'var(--text-color-calendar-week)',
            cursor: 'pointer', transition: 'all .18s', whiteSpace: 'nowrap',
          }}
        >
          {t('HrShowApproved')}
        </button>
        <div className="divider-v" />
        {/* KPI toggle — правая сторона */}
        <button
          type="button"
          className={`dt-btn-ghost${showKpi ? ' active' : ''}`}
          onClick={() => setShowKpi(v => !v)}
          title="KPI"
          style={{ fontSize: 11 }}
        >
          📊 KPI
        </button>
        <div className="divider-v" />

        <div ref={toolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
      </PageToolbar>

      {/* KPI cards — фиксированы, не скролятся */}
      {showKpi && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
          <StatCard icon={<EventNoteIcon sx={{ fontSize: 16 }} />}    label={t('HrLeave') as string}          value={leaveCount}    color="#43c98a" />
          <StatCard icon={<SickIcon sx={{ fontSize: 16 }} />}         label={t('HrSickness') as string}       value={sicknessCount} color="#f76f8e" />
          <StatCard icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}   label={t('HrLateness') as string}       value={latenessCount} color="#f4a942" />
          <StatCard icon={<HourglassTopIcon sx={{ fontSize: 16 }} />} label={t('HrStatus_pending') as string} value={pendingCount}  color="#f4a942" />
          <StatCard icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}  label={t('HrApproved') as string}       value={approvedCount} color="#43c98a" />
        </div>
      )}

      {/* Таблица — занимает остаток и скролится внутри */}
      <div className="ts-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <DataTable
          rows={filtered}
          getKey={r => r.id}
          columns={columns}
          filterable={false}
          storageKey="hr-absence-workflow"
          emptyText={t('Empty') as string}
          toolbarRef={toolbarSlot}
          noCard
          onDeleteMany={canManage ? async (ids) => {
            const res = await Promise.allSettled(ids.map(id => axios.delete(`hr-absence/${id}`)));
            loadList();
            if (res.some(r => r.status === 'rejected')) throw new Error('partial');
          } : undefined}
        />
      </div>

      <AbsenceRequestModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={loadList} allowPartial={allowPartial} users={users} editing={editing} timeStep={timeStep} />

      <Modal open={rejectFor != null} onClose={() => setRejectFor(null)}>
        <Box sx={modalBaseStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-color)', marginBottom: 'var(--space-l2)' }}>{t('HrRejectReason')}</h3>
          <textarea rows={3} className="timesheet-edit-input w-full" style={{ resize: 'none' }} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder={t('HrReason') as string} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-l3)', marginTop: 'var(--space-l2)' }}>
            <button type="button" className="dt-btn-ghost" onClick={() => setRejectFor(null)}>{t('Cancel')}</button>
            <button type="button" className="btn-primary" onClick={confirmReject}>{t('HrReject')}</button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default HrAbsenceWorkflow;
